import React from "react"
import { Typography, Divider, Row, Col, Tooltip, Collapse } from "antd"
import PropTypes from "prop-types"
import { useWatch } from "antd/es/form/Form"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { numberToCurrency } from "helpers/string"
import { InfoCircleOutlined } from "@ant-design/icons"
import { calculatePaymentBreakdown } from "utils/bookingCalculations"
import { computeDiscountAmount } from "utils/discountUtils"
import { colors } from "themes/variables"

import FlatRatePriceTooltip from "ui/FlatRatePriceTooltip"
import styles from "./BookingForm.module.css"

const { Text } = Typography

const PriceBreakdown = ({ form, booking, isEdit, isPriceDecreased, hasDepositPayment, appliedPromos = [] }) => {
  const { discount_amount, discount_code } = booking?.payment_summary ?? {}
  const { t } = useTranslation()
  const depositPercent = useWatch("depositPercent", form) || (booking?.deposit_percentage ? booking.deposit_percentage : "")
  const customDepositAmount = useWatch("customDepositAmount", form)
  const paymentOption = useWatch("paymentOption", form)
  const depositOnly = useWatch("depositOnly", form)
  const {
    experienceBookingData: { bookingSelections }
  } = useSelector((state) => state.experiences)

  const baseAmount = bookingSelections?.baseAmount ?? 0
  const addOnsTotal = bookingSelections?.addOnsTotal ?? 0
  const promoDiscount = appliedPromos.reduce((sum, p) => sum + computeDiscountAmount(p, baseAmount, addOnsTotal), 0)
  // When a new promo is applied, it REPLACES the original booking discount.
  const effectiveEditDiscount = isEdit && discount_amount > 0 && appliedPromos.length === 0 ? discount_amount : 0
  const totalDiscount = effectiveEditDiscount + promoDiscount

  // Calculate all payment values using the utility function
  const {
    percent,
    depositAmount,
    depositDueNow,
    balanceAmount,
    payment_history = [],
    refundDueNow
  } = calculatePaymentBreakdown({
    booking,
    bookingSelections,
    depositPercent,
    customDepositAmount,
    isPriceDecreased,
    paymentOption,
    discountAmount: totalDiscount
  })

  const breakdown = bookingSelections?.breakdown ?? []
  const taxIndex = breakdown.findIndex((item) => item?.isTaxFee || item?.label?.toLowerCase().includes("tax"))
  const beforeTax = taxIndex >= 0 ? breakdown.slice(0, taxIndex) : breakdown
  const taxAndAfter = taxIndex >= 0 ? breakdown.slice(taxIndex) : []

  const renderBreakdownItem = (item) =>
    item?.children?.length > 0 ? (
      <Collapse
        key={`${item?.label}-${item?.amount}`}
        ghost
        style={{ marginTop: 8 }}
        items={[
          {
            key: "tax-fees",
            styles: {
              header: { padding: 0 },
              body: { padding: "4px 0 0 16px" }
            },
            label: (
              <Row justify="space-between">
                <Col>
                  <Text>{item?.label}</Text>
                </Col>
                <Col>
                  <Text>{item?.amount}</Text>
                </Col>
              </Row>
            ),
            children: item.children.map((child) => (
              <Row key={`${child?.label}-${child?.amount}`} justify="space-between" style={{ marginTop: 4 }}>
                <Col>
                  <Text type="secondary">{child?.label}</Text>
                </Col>
                <Col>
                  <Text type="secondary">{child?.amount}</Text>
                </Col>
              </Row>
            ))
          }
        ]}
      />
    ) : (
      <Row key={`${item?.label}-${item?.amount}`} justify="space-between" style={{ marginTop: 8 }}>
        <Col>
          <Text>{item?.label}</Text>
          {item?.pricingDetails ? (
            <FlatRatePriceTooltip {...item.pricingDetails} />
          ) : (
            item?.info && (
              <Tooltip title={item.info}>
                <InfoCircleOutlined className="ml-xxs" style={{ color: "var(--color-primary)" }} />
              </Tooltip>
            )
          )}
        </Col>
        <Col>
          <Text>{item?.amount}</Text>
        </Col>
      </Row>
    )

  const totalAfterDiscounts = Math.max(0, (bookingSelections?.amount ?? 0) - effectiveEditDiscount - promoDiscount)
  const hasAnyDiscount = appliedPromos.length > 0 || effectiveEditDiscount > 0
  const showDepositDueNow = (isEdit && hasDepositPayment && !isPriceDecreased) || (!isEdit && depositOnly)
  const showRemainingBalance = showDepositDueNow && balanceAmount > 0

  return (
    <div>
      {beforeTax.map(renderBreakdownItem)}

      {effectiveEditDiscount > 0 && (
        <Row justify="space-between" className={styles.priceRow}>
          <Col>
            <Text type="danger">{discount_code ? t("booking.discount_with_code", { code: discount_code }) : t("booking.discount")}</Text>
          </Col>
          <Col>
            <Text type="danger">-{numberToCurrency(effectiveEditDiscount)}</Text>
          </Col>
        </Row>
      )}

      {addOnsTotal > 0 && (
        <Row justify="space-between" className={styles.priceRow}>
          <Col>
            <Text>{t("booking.add_ons.title")}</Text>
          </Col>
          <Col>
            <Text>{numberToCurrency(addOnsTotal)}</Text>
          </Col>
        </Row>
      )}

      {taxAndAfter.map(renderBreakdownItem)}

      <Divider className={styles.priceDivider} />

      {appliedPromos.map((promo) => (
        <Row key={promo.code} justify="space-between" className={styles.priceRow}>
          <Col>
            <Text type="secondary">{promo.type === "voucher" ? t("booking.gift_card_with_code", { code: promo.code }) : `${promo.name} (${promo.code})`}</Text>
          </Col>
          <Col>
            <Text style={{ color: "var(--color-primary)" }}>
              -{numberToCurrency(computeDiscountAmount(promo, baseAmount, addOnsTotal))}
            </Text>
          </Col>
        </Row>
      ))}

      <Row justify="space-between" className={hasAnyDiscount ? styles.priceRow : styles.priceRowFlush}>
        <Col>
          <Text strong>{hasAnyDiscount ? t("booking.labels.total") + " (after savings)" : t("booking.labels.total")}</Text>
        </Col>
        <Col>
          <Text strong>{numberToCurrency(totalAfterDiscounts)}</Text>
        </Col>
      </Row>
      {percent > 0 && (
        <>
          {isEdit &&
            payment_history?.map((history) => {
              const { type, method, last4, amount, id } = history || {}
              const isRefund = type?.toLowerCase() === "refund"
              const key = id || `${type}-${method}-${last4}-${amount}`
              return (
                <Row key={key} justify="space-between" style={{ marginTop: 8 }}>
                  <Col>
                    <Text>
                      {type || ""} ({method || ""}
                      {last4 && ` **** ${last4}`})
                    </Text>
                  </Col>
                  <Col>
                    <Text color={isRefund ? colors.error : colors.textPrimary}>{amount ? numberToCurrency(amount) : ""}</Text>
                  </Col>
                </Row>
              )
            })}
          {showDepositDueNow && (
            <Row justify="space-between" style={{ marginTop: 8 }}>
              <Col>
                <Text>{isEdit ? t("booking.labels.deposit_due_now") : depositOnly && t("booking.labels.pay_now_deposit")}</Text>
              </Col>
              <Col>
                <Text>{numberToCurrency(isEdit ? depositDueNow : depositOnly && depositAmount)}</Text>
              </Col>
            </Row>
          )}
          {showRemainingBalance && (
            <Row justify="space-between" style={{ marginTop: 8 }}>
              <Col>
                <Text strong>{t("booking.labels.remaining_balance")}</Text>
              </Col>
              <Col>
                <Text strong>{numberToCurrency(balanceAmount)}</Text>
              </Col>
            </Row>
          )}
        </>
      )}
      {isEdit && refundDueNow > 0 && (
        <Row justify="space-between" style={{ marginTop: 8 }}>
          <Col>
            <Text strong>{t("booking.labels.refund_due")}</Text>
          </Col>
          <Col>
            <Text strong>{numberToCurrency(refundDueNow)}</Text>
          </Col>
        </Row>
      )}
    </div>
  )
}

PriceBreakdown.propTypes = {
  form: PropTypes.object.isRequired,
  booking: PropTypes.object.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isPriceDecreased: PropTypes.bool.isRequired,
  hasDepositPayment: PropTypes.bool.isRequired,
  appliedPromos: PropTypes.array
}

export default PriceBreakdown
